import { useRef, useState } from 'react';
import { adresseApi } from '../../api/client';
import { DEVISE, fmt } from '../../lib/currency';
import { ouvreDocument } from '../../api/documents';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { BoutonHaut } from '../../components/ui/BoutonHaut.jsx';
import { tr, trf } from '../../lib/i18n';
import { prixCatalogue } from '../../lib/vat';
import { C } from '../../theme/palette';
import { EnvoiCatalogue, EnvoiCatalogueNeutre } from './EnvoiCatalogue.jsx';

/**
 * Le choix de la société à qui envoyer le catalogue, avant l'envoi à ses
 * personnes (patron, directeur, techniciens…).
 *
 * @param {object} props
 * @param {object[]} props.clients clients enregistrés
 * @param {(id: number) => void} props.onChoisi société choisie
 * @param {() => void} props.onFermer retour au catalogue
 * @returns {import('react').ReactElement} la liste à choisir
 */
function ChoixSociete({ clients, onChoisi, onFermer }) {
  const [q, setQ] = useState('');
  const cherche = q.trim().toLowerCase();
  const liste = (clients || [])
    .filter((c) => Number.isInteger(c.id))
    .filter(
      (c) =>
        !cherche ||
        String(c.nom || '')
          .toLowerCase()
          .includes(cherche),
    );
  return (
    <div
      className="cat-noprint"
      role="dialog"
      aria-label="Choisir la société"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: C.bg,
        overflow: 'auto',
        padding: 'calc(14px + var(--st-haut)) 16px calc(30px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <button
          type="button"
          onClick={onFermer}
          style={{
            background: 'none',
            border: 'none',
            color: C.navy,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            padding: '4px 0 10px',
          }}
        >
          ← Retour au catalogue
        </button>
        <div style={{ fontSize: 15, fontWeight: 900, color: C.ink, marginBottom: 4 }}>
          ✉️ À quelle société envoyer le catalogue ?
        </div>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 10 }}>
          Ensuite, tu coches les personnes : patron, directeur, techniciens… Un seul envoi.
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔎 Chercher un client…"
          aria-label="Chercher un client"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: '1.5px solid ' + C.border,
            borderRadius: 12,
            padding: '11px 12px',
            fontSize: 13,
            marginBottom: 10,
          }}
        />
        {liste.length === 0 && (
          <div style={{ fontSize: 10.5, color: C.muted, fontStyle: 'italic' }}>
            Aucun client trouvé.
          </div>
        )}
        {liste.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onChoisi(c.id)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              background: C.white,
              border: '1.5px solid ' + C.border,
              borderRadius: 12,
              padding: '11px 13px',
              marginBottom: 7,
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>{c.nom}</div>
            <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>
              {(c.contacts || []).filter((p) => p.email).length
                ? trf(
                    '{0} personne(s) dans la fiche',
                    (c.contacts || []).filter((p) => p.email).length,
                  )
                : c.npa || ''}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * « 📤 Partager mon catalogue » du stock, tel que la maquette V288 le montre :
 * les articles par rayon, au prix de vente hors TVA (TVA comprise au choix),
 * à imprimer ou à enregistrer en PDF pour un prospect.
 *
 * <p>Les tarifs remisés d'un client restent dans l'onglet Remises : ici, ce
 * sont les prix de base.
 *
 * @param {object} props
 * @param {object[]} props.stock articles, dans l'ordre d'affichage
 * @param {object} props.soc société affichée en tête
 * @param {number} props.tauxDefaut taux de l'article qui n'en a pas en propre
 * @param {(art: object) => string} props.vignette adresse de la photo d'un article
 * @param {object[]} [props.clients] clients, pour envoyer le catalogue à une société
 * @param {Function} [props.setClients] mise à jour des clients (personnes de la fiche)
 * @param {() => void} props.onClose fermeture
 * @returns {import('react').ReactElement} l'écran du catalogue
 */
export function CatalogueStock({ stock, soc, tauxDefaut, vignette, clients, setClients, onClose }) {
  const [catTTC, setCatTTC] = useState(false);
  // Envoi à une société : d'abord le choix de la société, puis ses personnes.
  const [choixSociete, setChoixSociete] = useState(false);
  // Le catalogue neutre, au prix public, par e-mail à qui l'on veut.
  const [envoiNeutre, setEnvoiNeutre] = useState(false);
  const [societeId, setSocieteId] = useState(null);
  const societe = (clients || []).find((c) => c.id === societeId);
  const rayons = [...new Set((stock || []).map((a) => a.cat || 'Autres'))];
  const defile = useRef(null);
  return (
    <PleinEcran>
      <div
        ref={defile}
        style={{
          position: 'fixed',
          inset: 0,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          // Sous la visionneuse des PDF (10000), qui s'ouvre par-dessus.
          zIndex: 9000,
          background: '#fff',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <style>
          {
            '@media print{@page{size:A4;margin:14mm}body *{visibility:hidden}.cat-sheet,.cat-sheet *{visibility:visible}.cat-sheet{position:absolute;left:0;top:0;width:100%}.cat-noprint{display:none!important}}'
          }
        </style>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: 16 }}>
          {/* Les boutons restent en haut pendant qu'on descend, sous l'heure
            et la batterie de l'iPhone : retour et envois toujours à portée. */}
          <div
            className="cat-noprint"
            style={{
              display: 'flex',
              gap: 9,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: '#fff',
              margin: '-16px -16px 14px',
              padding: 'calc(12px + var(--st-haut)) 16px 12px',
              boxShadow: '0 2px 8px rgba(15,26,46,.08)',
            }}
          >
            <div
              role="button"
              // Le vrai PDF du catalogue, dans SwissTech : l'enregistrer, le
              // partager ou l'imprimer. L'impression de la page était refusée
              // par l'iPhone (« L'impression automatique n'est pas autorisée »).
              onClick={() =>
                ouvreDocument('/catalogue/document', { ttc: catTTC }).catch(() => undefined)
              }
              style={{
                flex: 1,
                textAlign: 'center',
                background: C.navy,
                color: '#fff',
                borderRadius: 12,
                padding: 13,
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              🖨 Imprimer / PDF
            </div>
            <div
              role="button"
              onClick={() => setEnvoiNeutre(true)}
              style={{
                flex: 1,
                textAlign: 'center',
                background: C.teal,
                color: '#fff',
                borderRadius: 12,
                padding: 13,
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {/* Trait d'union insécable : le bouton se coupait en « e- / mail ». */}
              {tr('✉️ Envoyer par e-mail').replace('e-mail', 'e\u2011mail')}
            </div>
            {setClients && (
              <div
                role="button"
                onClick={() => setChoixSociete(true)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  background: C.green,
                  color: '#fff',
                  borderRadius: 12,
                  padding: 13,
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                ✉️ Envoyer à une société
              </div>
            )}
            <div
              onClick={onClose}
              style={{
                padding: '13px 20px',
                textAlign: 'center',
                background: C.navyXL,
                color: C.ink,
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Fermer
            </div>
          </div>
          <div
            className="cat-sheet"
            style={{
              border: '1px solid ' + C.border,
              borderRadius: 18,
              overflow: 'hidden',
              boxShadow: '0 2px 14px rgba(20,29,46,.08)',
            }}
          >
            <div style={{ background: C.navy, color: '#fff', padding: '18px' }}>
              {soc?.logo ? (
                <img
                  src={soc.logo}
                  alt=""
                  style={{ height: 34, marginBottom: 9, borderRadius: 7 }}
                />
              ) : null}
              <div style={{ fontSize: 16, fontWeight: 800 }}>{soc?.nom || 'Mon entreprise'}</div>
              <div style={{ fontSize: 11, color: '#8ea0b8', marginTop: 2, letterSpacing: 0.4 }}>
                Catalogue · Tarifs {new Date().getFullYear()}
              </div>
            </div>
            <div
              style={{
                background: C.tealL,
                borderBottom: '1px solid ' + C.border,
                padding: '11px 16px',
                fontSize: 10.5,
                color: C.teal,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              ℹ️ Prix affichés {catTTC ? <b>TVA comprise</b> : <b>hors TVA</b>}.
            </div>
            <div
              className="cat-noprint"
              role="switch"
              aria-checked={catTTC}
              onClick={() => setCatTTC((v) => !v)}
              style={{
                padding: '9px 16px',
                borderBottom: '1px solid ' + C.border,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                background: '#fff',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 999,
                  background: catTTC ? C.green : C.border,
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 3,
                    left: catTTC ? 21 : 3,
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                  }}
                />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.ink }}>
                Afficher les prix TTC (TVA comprise)
              </span>
            </div>
            {rayons.map((cat) => (
              <div key={cat}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '.5px',
                    color: C.muted,
                    textTransform: 'uppercase',
                    padding: '14px 16px 8px',
                  }}
                >
                  {cat}
                </div>
                {(stock || [])
                  .filter((a) => (a.cat || 'Autres') === cat)
                  .map((a) => {
                    const taux = a.tva != null ? +a.tva : tauxDefaut;
                    const img = vignette(a);
                    return (
                      <div
                        key={a.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          padding: '12px 16px',
                          borderTop: '1px solid ' + C.border,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: 9,
                            alignItems: 'center',
                            paddingRight: 12,
                          }}
                        >
                          {img ? (
                            <img
                              src={adresseApi(img)}
                              alt=""
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1px solid ' + C.border,
                              }}
                            />
                          ) : null}
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>
                              {a.nom}
                            </div>
                            {a.ref ? (
                              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
                                Réf. {a.ref}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 900,
                              color: C.navy,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {DEVISE} {fmt(prixCatalogue(+a.pv || 0, taux, catTTC))}
                          </div>
                          <div style={{ fontSize: 9.5, color: C.muted, fontWeight: 600 }}>
                            / {a.u || 'pièce'}
                          </div>
                          <div
                            style={{
                              fontSize: 8,
                              color: C.muted,
                              fontStyle: 'italic',
                              marginTop: 1,
                            }}
                          >
                            {catTTC ? 'TVA incl.' : 'hors TVA'}
                            {a.tva != null ? ' (' + a.tva + '%)' : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ))}
            <div
              style={{
                padding: '14px 16px',
                background: C.navyXL,
                borderTop: '1px solid ' + C.border,
                fontSize: 9.5,
                color: C.grey,
                lineHeight: 1.6,
              }}
            >
              Tous les prix sont indicatifs, <b>{catTTC ? 'TVA comprise' : 'hors TVA'}</b>.{' '}
              {soc?.adr ? '📍 ' + soc.adr + ' ' + (soc.npa || '') : ''}
              {soc?.tel ? ' · ☎ ' + soc.tel : ''}
            </div>
          </div>
          <div style={{ height: 20 }} />
        </div>
        {choixSociete && !societe && (
          <ChoixSociete
            clients={clients}
            onChoisi={setSocieteId}
            onFermer={() => setChoixSociete(false)}
          />
        )}
        {envoiNeutre && (
          <EnvoiCatalogueNeutre ttc={catTTC} onFermer={() => setEnvoiNeutre(false)} />
        )}
        {societe && (
          <EnvoiCatalogue
            key={societe.id}
            client={societe}
            ttc={catTTC}
            setClients={setClients}
            onFermer={() => {
              setSocieteId(null);
              setChoixSociete(false);
            }}
          />
        )}
        <BoutonHaut cible={defile} />
      </div>
    </PleinEcran>
  );
}
